import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

export type StatusTone =
  | 'upcoming'
  | 'completed'
  | 'draft'
  | 'finalized'
  | 'cancelled'
  | 'no_show'
  | 'locked'
  | 'neutral'
  | 'coral'
  | 'red';

const STATUS_CLASS: Record<StatusTone, string> = {
  upcoming: 'bg-ds-teal-tint text-ds-teal',
  completed: 'bg-ds-green-tint text-ds-green-ink',
  draft: 'bg-ds-amber-tint text-ds-amber-ink',
  finalized: 'bg-ds-green-tint text-ds-green-ink',
  cancelled: 'bg-ds-neutral-tint text-ds-muted',
  no_show: 'bg-ds-coral-tint text-ds-coral-ink',
  locked: 'bg-ds-amber-tint text-ds-amber-ink',
  neutral: 'bg-ds-neutral-tint text-ds-muted',
  coral: 'bg-ds-coral-tint text-ds-coral-ink',
  red: 'bg-ds-red-tint text-ds-red-ink',
};

export function StatusChip({
  tone,
  children,
  className,
}: {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        TYPE.meta,
        'inline-flex h-6 items-center gap-1.5 rounded-full px-2.5',
        STATUS_CLASS[tone],
        className,
      )}
    >
      {tone === 'locked' ? <Lock className="h-3 w-3" aria-hidden /> : null}
      {children}
    </span>
  );
}

export function PaymentChip({
  children,
  icon,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        TYPE.meta,
        'inline-flex h-6 items-center gap-1 rounded-full bg-ds-card px-s2 text-ds-text shadow-[inset_0_0_0_1px_var(--line-strong)]',
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

const TYPE_MONO_CHIP = 'font-mono font-medium';

export function CodeChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        TYPE.meta,
        TYPE_MONO_CHIP,
        'inline-flex h-6 items-center rounded-full bg-ds-neutral-tint px-s2 text-ds-muted',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function FilterChip({
  pressed,
  count,
  children,
  onClick,
  className,
  title,
}: {
  pressed?: boolean;
  count?: number;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  /** Full label when the chip text is shortened. */
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        TYPE.control,
        'inline-flex h-8 items-center gap-s1 rounded-full border px-s3',
        pressed
          ? 'border-ds-teal bg-ds-teal-tint font-semibold text-ds-teal'
          : 'border-ds-field bg-ds-card font-medium text-ds-text',
        className,
      )}
    >
      {children}
      {count != null ? (
        <span
          className={cn('font-semibold tabular-nums', pressed ? 'text-ds-teal' : 'text-ds-muted')}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}
